import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type SegOption = string | { value: string; label: ReactNode };

export interface SegProps {
  value: string;
  options: readonly SegOption[];
  onChange: (v: string) => void;
  ariaLabel: string;
  className?: string;
  accent?: string;
  /**
   * 'touch' raises the row to the 44px minimum index.css mandates for mobile controls and
   * drops the compact opt-out. Opt-in: 'default' is byte-for-byte the previous behaviour,
   * so the MLB surfaces already using Seg are unaffected.
   */
  size?: 'default' | 'touch';
  /**
   * Selected-state treatment. OPT-IN: 'outline' is byte-for-byte the previous behaviour,
   * so the five existing Seg surfaces (four MLB, NFL FilterSheet) are unaffected.
   *
   * 'filled' is solid brand orange with DARK text.
   *
   * ── WHY THIS DOES NOT USE text-primary-foreground ────────────────────────
   *
   * The Matchups week pills render `bg-primary text-primary-foreground`, and copying
   * that here would have been the obvious move. It is wrong. `--primary-foreground`
   * is `0 0% 100%` under :root and `222 47% 6%` under .dark, so those pills are
   * WHITE ON ORANGE for any light-theme user -- the app defaults to dark but light is
   * reachable via the `sf-theme` key read in index.html.
   *
   * The brand rule is dark #12130F on orange. Five draft-room surfaces already encode
   * it literally (NFLDraftRoom, MobileDraftRoom, MobileChatPanel, RightRail,
   * PlayersTable), and that is the treatment reused here -- theme-independent, and
   * the one that actually matches the palette.
   */
  variant?: 'outline' | 'filled';
}

const valueOf = (opt: SegOption) => (typeof opt === 'string' ? opt : opt.value);

export function Seg({
  value,
  options,
  onChange,
  ariaLabel,
  className,
  accent = 'hsl(var(--primary))',
  size = 'default',
  variant = 'outline',
}: SegProps) {
  const touch = size === 'touch';
  const filled = variant === 'filled';
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = (i + dir + options.length) % options.length;
    refs.current[next]?.focus();
    onChange(valueOf(options[next]));
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'flex items-stretch w-full min-w-0 p-0.5 rounded-full bg-muted border border-border',
        touch ? 'min-h-[44px]' : 'h-[34px]',
        className,
      )}
    >
      {options.map((opt, i) => {
        const optValue = valueOf(opt);
        const active = optValue === value;
        return (
          <button
            key={optValue}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(optValue)}
            onKeyDown={(e) => onKeyDown(e, i)}
            // Opt out of the mobile-wide 44px min-height in index.css. The tap
            // target is the 34px radiogroup row, not each radio. Without this,
            // the inner button overflows the outer pill and the inset shadow
            // (which sits on the button's edge) overshoots the pill border.
            {...(touch ? {} : { 'data-compact': '' })}
            className={cn(
              'flex-1 min-w-0 rounded-full border-0 cursor-pointer relative',
              // The filled variant paints the SELECTED button, so it cannot be
              // unconditionally transparent the way the outline variant is.
              !(active && filled) && 'bg-transparent',
              // touch mode packs three word-labels into a 390px pill; px-2 overflowed it by
              // ~6px per item, so the label clipped its own container instead of truncating.
              touch ? 'px-1' : 'px-2',
              'flex items-center justify-center gap-1.5',
              'text-[12px] font-semibold whitespace-nowrap',
              'transition-[box-shadow,color] duration-150',
              'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[hsl(var(--ring))]',
              // Unselected is identical in both variants, deliberately -- only the
              // selected treatment changes.
              !active && 'text-muted-foreground',
              active && (filled
                // Same fill the draft-room controls use. shadow-md matches the lift on
                // the Matchups pills so the two surfaces read as one system.
                ? 'bg-primary text-[#12130f] shadow-md'
                : 'text-foreground'),
            )}
            style={active && !filled ? { boxShadow: `inset 0 0 0 1px ${accent}` } : undefined}
          >
            {typeof opt === 'string' ? (
              <span className="min-w-0 truncate">{opt}</span>
            ) : (
              opt.label
            )}
          </button>
        );
      })}
    </div>
  );
}
